export type Dimension =
  | 'Estratégia'
  | 'Portfólio'
  | 'Processos'
  | 'Cultura'
  | 'Proteção'

export interface Option {
  value: number
  label: string
}

export interface Question {
  id: string
  dimension: Dimension
  text: string
  options: Option[]
}

export const questions: Question[] = [
  // 1. Estratégia de PI
  {
    id: 'strat_1',
    dimension: 'Estratégia',
    text: 'Como sua empresa define os objetivos de Propriedade Intelectual (PI)?',
    options: [
      { value: 1, label: 'Não temos objetivos de PI definidos.' },
      { value: 2, label: 'Objetivos informais e reativos a problemas.' },
      {
        value: 3,
        label: 'Objetivos documentados, mas isolados da estratégia de negócio.',
      },
      {
        value: 4,
        label: 'Objetivos alinhados com a estratégia geral da empresa.',
      },
      {
        value: 5,
        label:
          'Objetivos de PI são parte central da estratégia de inovação e negócios.',
      },
    ],
  },
  {
    id: 'strat_2',
    dimension: 'Estratégia',
    text: 'Qual é o orçamento dedicado à gestão e proteção de PI?',
    options: [
      { value: 1, label: 'Não há orçamento específico.' },
      { value: 2, label: 'Orçamento ad-hoc, liberado conforme demanda.' },
      { value: 3, label: 'Orçamento anual fixo para manutenção básica.' },
      { value: 4, label: 'Orçamento planejado cobrindo proteção e litígios.' },
      {
        value: 5,
        label:
          'Orçamento estratégico flexível para aquisições e expansão global.',
      },
    ],
  },
  {
    id: 'strat_3',
    dimension: 'Estratégia',
    text: 'Como a PI influencia o desenvolvimento de novos produtos?',
    options: [
      { value: 1, label: 'Não influencia.' },
      { value: 2, label: 'Verificação tardia (apenas antes do lançamento).' },
      { value: 3, label: 'Consultas pontuais durante o desenvolvimento.' },
      {
        value: 4,
        label: 'Análise de liberdade de operação (FTO) em etapas chave.',
      },
      {
        value: 5,
        label: 'PI integrada desde a ideação (Design Around, FTO precoce).',
      },
    ],
  },

  // 2. Gestão de Portfólio
  {
    id: 'port_1',
    dimension: 'Portfólio',
    text: 'Como a empresa mantém o registro de seus ativos de PI?',
    options: [
      { value: 1, label: 'Não mantemos registros organizados.' },
      { value: 2, label: 'Planilhas dispersas ou arquivos físicos.' },
      { value: 3, label: 'Planilha centralizada atualizada periodicamente.' },
      { value: 4, label: 'Software de gestão ou banco de dados estruturado.' },
      {
        value: 5,
        label: 'Sistema integrado com alertas automáticos e dashboards.',
      },
    ],
  },
  {
    id: 'port_2',
    dimension: 'Portfólio',
    text: 'Com que frequência o portfólio de PI é revisado?',
    options: [
      { value: 1, label: 'Nunca revisamos.' },
      { value: 2, label: 'Apenas quando surgem problemas ou renovações.' },
      { value: 3, label: 'Anualmente, para fins contábeis.' },
      { value: 4, label: 'Semestralmente, avaliando alinhamento comercial.' },
      {
        value: 5,
        label:
          'Trimestralmente, com decisões de manutenção/abandono estratégicas.',
      },
    ],
  },
  {
    id: 'port_3',
    dimension: 'Portfólio',
    text: 'A empresa monetiza seus ativos de PI (licenciamento, venda)?',
    options: [
      { value: 1, label: 'Não, usamos apenas para proteção própria.' },
      { value: 2, label: 'Raramente, sem processo definido.' },
      { value: 3, label: 'Ocasionalmente, reativamente a ofertas.' },
      {
        value: 4,
        label: 'Sim, buscamos ativamente oportunidades de licenciamento.',
      },
      {
        value: 5,
        label:
          'Sim, temos um fluxo de receita recorrente e estruturado via PI.',
      },
    ],
  },

  // 3. Processos e Governança
  {
    id: 'proc_1',
    dimension: 'Processos',
    text: 'Existe um processo formal para capturar invenções dos colaboradores?',
    options: [
      { value: 1, label: 'Não existe processo.' },
      { value: 2, label: 'Comunicação informal (e-mail, conversa).' },
      { value: 3, label: 'Formulário padrão, mas pouco utilizado.' },
      { value: 4, label: 'Processo estruturado de notificação de invenção.' },
      {
        value: 5,
        label: 'Processo digital automatizado com incentivos claros.',
      },
    ],
  },
  {
    id: 'proc_2',
    dimension: 'Processos',
    text: 'Quem é responsável pelas decisões de PI na empresa?',
    options: [
      { value: 1, label: 'Ninguém especificamente.' },
      { value: 2, label: 'Jurídico ou P&D, sem clareza.' },
      { value: 3, label: 'Um gestor designado, mas sem comitê.' },
      { value: 4, label: 'Comitê de PI multidisciplinar.' },
      {
        value: 5,
        label: 'Comitê executivo com participação da diretoria (C-level).',
      },
    ],
  },
  {
    id: 'proc_3',
    dimension: 'Processos',
    text: 'Como são tratados os contratos de confidencialidade (NDAs) e cláusulas de PI?',
    options: [
      { value: 1, label: 'Não usamos NDAs regularmente.' },
      { value: 2, label: 'Usamos modelos padrão sem revisão.' },
      { value: 3, label: 'Jurídico revisa contratos importantes.' },
      {
        value: 4,
        label: 'Processo padrão para todos os parceiros e fornecedores.',
      },
      {
        value: 5,
        label:
          'Gestão rigorosa de segredos comerciais e contratos em toda a cadeia.',
      },
    ],
  },

  // 4. Cultura e Capacitação
  {
    id: 'cult_1',
    dimension: 'Cultura',
    text: 'Qual o nível de conhecimento sobre PI entre os colaboradores?',
    options: [
      { value: 1, label: 'Conhecimento inexistente ou muito baixo.' },
      { value: 2, label: 'Conhecimento básico apenas na liderança.' },
      { value: 3, label: 'Equipes de P&D têm noções básicas.' },
      { value: 4, label: 'Treinamentos regulares para áreas chave.' },
      { value: 5, label: 'Cultura de PI disseminada em toda a organização.' },
    ],
  },
  {
    id: 'cult_2',
    dimension: 'Cultura',
    text: 'A empresa oferece incentivos para inventores?',
    options: [
      { value: 1, label: 'Não há incentivos.' },
      { value: 2, label: 'Reconhecimento verbal ou simbólico.' },
      { value: 3, label: 'Bônus ad-hoc discricionários.' },
      {
        value: 4,
        label: 'Política de premiação definida para patentes depositadas.',
      },
      {
        value: 5,
        label: 'Programa robusto de remuneração e reconhecimento de carreira.',
      },
    ],
  },
  {
    id: 'cult_3',
    dimension: 'Cultura',
    text: 'Como a PI é vista pela liderança da empresa?',
    options: [
      { value: 1, label: 'Como um custo burocrático.' },
      { value: 2, label: 'Como uma necessidade legal.' },
      { value: 3, label: 'Como uma ferramenta de proteção.' },
      { value: 4, label: 'Como um ativo de valor.' },
      { value: 5, label: 'Como vantagem competitiva essencial.' },
    ],
  },

  // 5. Proteção e Defesa
  {
    id: 'prot_1',
    dimension: 'Proteção',
    text: 'A empresa monitora infrações de terceiros contra sua PI?',
    options: [
      { value: 1, label: 'Não monitoramos.' },
      { value: 2, label: 'Apenas se recebermos denúncias.' },
      { value: 3, label: 'Monitoramento manual esporádico.' },
      { value: 4, label: 'Monitoramento regular de concorrentes diretos.' },
      { value: 5, label: 'Monitoramento global sistemático e automatizado.' },
    ],
  },
  {
    id: 'prot_2',
    dimension: 'Proteção',
    text: 'Qual a postura da empresa em relação a riscos de infração de terceiros?',
    options: [
      { value: 1, label: 'Ignoramos os riscos.' },
      { value: 2, label: 'Reagimos apenas quando notificados.' },
      { value: 3, label: 'Analisamos riscos em grandes lançamentos.' },
      { value: 4, label: 'Realizamos buscas de anterioridade regularmente.' },
      {
        value: 5,
        label: 'Gestão proativa de riscos e liberdade de operação (FTO).',
      },
    ],
  },
  {
    id: 'prot_3',
    dimension: 'Proteção',
    text: 'A empresa protege seus ativos em mercados internacionais?',
    options: [
      { value: 1, label: 'Apenas no Brasil.' },
      { value: 2, label: 'Em 1 ou 2 países se houver exportação.' },
      { value: 3, label: 'Nos principais mercados atuais.' },
      { value: 4, label: 'Estratégia de proteção alinhada à expansão futura.' },
      {
        value: 5,
        label: 'Proteção global estratégica cobrindo mercados e concorrentes.',
      },
    ],
  },
]
